import { useEffect, useRef, useState } from "react";
import { api, type Paciente } from "../api.ts";
import { recorteInicial, ASPECTO_RECORTE, type Caja } from "../../../dominio/src/index.ts";
import { AvisoError, useAccion } from "../pantallas/comun.tsx";
import { preparar } from "../pantallas/Fotos.tsx";
import { ubicarCara } from "./ubicarCara.ts";
import { EnviarWhatsApp } from "./EnviarWhatsApp.tsx";
import { GuardarPdf } from "./GuardarPdf.tsx";

const MAX_LADO = 2000;      // la foto se reduce para trabajar sin gastar memoria
const ANCHO_ENVIO = 1024;   // ancho del recorte que se envía a la IA

type Tipo = "blanqueamiento" | "sonrisa";
type Nivel = "suave" | "medio" | "maximo";
type Estilo = "natural" | "armonica" | "hollywood";
const NIVELES: [Nivel, string][] = [["suave", "Suave (2 tonos)"], ["medio", "Medio (4 tonos)"], ["maximo", "Máximo"]];
const ESTILOS: [Estilo, string][] = [["natural", "Natural"], ["armonica", "Armónica (proporciones)"], ["hollywood", "Más blanca y pareja"]];

const cargarImagen = (src: string) => new Promise<HTMLImageElement>((ok, mal) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => mal(new Error("No se pudo leer la imagen.")); i.src = src; });

/**
 * Sonrisa con IA: Donna ubica la boca, envía SOLO ese recorte (de la nariz al mentón) a la IA de imágenes de Google con la clave de la clínica,
 * y pega el resultado sobre la foto original con los bordes suavizados. Es solo una referencia: queda rotulada como simulación.
 */
export function SimulacionIA({ paciente, url, etiqueta, alCerrar, alGuardar }: { paciente: Paciente; url: string; etiqueta: string; alCerrar: () => void; alGuardar: () => void }) {
  const base = useRef<HTMLImageElement | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  const [tam, setTam] = useState<{ w: number; h: number } | null>(null);
  const [caja, setCaja] = useState<Caja | null>(null);
  // borde interno de los labios: solo lo que queda dentro de la boca abierta se toma de la IA (los labios nunca cambian)
  const abertura = useRef<{ x: number; y: number }[] | null>(null);
  const [aviso, setAviso] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [listaIA, setListaIA] = useState<boolean | null>(null);
  const [tipo, setTipo] = useState<Tipo>("blanqueamiento");
  const [nivel, setNivel] = useState<Nivel>("medio");
  const [estilo, setEstilo] = useState<Estilo>("natural");
  const [generando, setGenerando] = useState(false);
  const [despues, setDespues] = useState<string | null>(null);
  const [corte, setCorte] = useState(50);
  const [guardado, setGuardado] = useState<{ id: string } | null>(null);
  const [arrastre, setArrastre] = useState<{ modo: "mover" | "esquina"; ox: number; oy: number; caja0: Caja } | null>(null);
  const a = useAccion();

  useEffect(() => { api<{ configurada: boolean }>("GET", "/api/gemini/estado").then((r) => setListaIA(r.configurada)).catch(() => setListaIA(false)); }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const im = await cargarImagen(url);
        if (!vivo) return;
        const k = Math.min(1, MAX_LADO / Math.max(im.naturalWidth, im.naturalHeight));
        const w = Math.round(im.naturalWidth * k), h = Math.round(im.naturalHeight * k);
        base.current = im; setTam({ w, h }); setCaja(recorteInicial(w, h));
        setBuscando(true);
        const r = await ubicarCara(im, w, h).catch(() => null);
        if (!vivo) return;
        abertura.current = r?.abertura ?? null;
        if (r) { setCaja(r.recorte); setAviso("Donna ubicó la boca. Revisa que el recuadro cubra de la nariz al mentón y muévelo si hace falta."); }
        else setAviso("No se encontró la cara: se puso un recuadro en la parte baja de la foto. Muévelo y ajústalo sobre la boca (de la nariz al mentón).");
      } catch { if (vivo) setAviso("No se pudo abrir la foto."); }
      finally { if (vivo) setBuscando(false); }
    })();
    return () => { vivo = false; };
  }, [url]);

  const aPixeles = (ev: React.PointerEvent | PointerEvent) => {
    const r = svg.current!.getBoundingClientRect();
    return { x: ((ev.clientX - r.left) / r.width) * tam!.w, y: ((ev.clientY - r.top) / r.height) * tam!.h };
  };
  const limitar = (c: Caja): Caja => {
    const w = Math.min(Math.max(c.w, tam!.w * 0.12), tam!.w, tam!.h * ASPECTO_RECORTE), h = w / ASPECTO_RECORTE;
    return { x: Math.min(Math.max(c.x, 0), tam!.w - w), y: Math.min(Math.max(c.y, 0), tam!.h - h), w, h };
  };
  useEffect(() => {
    if (!arrastre || !tam) return;
    const mover = (ev: PointerEvent) => {
      const p = aPixeles(ev);
      const c0 = arrastre.caja0;
      if (arrastre.modo === "mover") setCaja(limitar({ ...c0, x: c0.x + (p.x - arrastre.ox), y: c0.y + (p.y - arrastre.oy) }));
      else {
        // esquina inferior derecha: cambia el tamaño manteniendo la proporción 4:3 y la esquina superior izquierda
        const w = Math.max(p.x - c0.x, 20);
        setCaja(limitar({ x: c0.x, y: c0.y, w, h: w / ASPECTO_RECORTE }));
      }
    };
    const soltar = () => setArrastre(null);
    addEventListener("pointermove", mover); addEventListener("pointerup", soltar); addEventListener("pointercancel", soltar);
    return () => { removeEventListener("pointermove", mover); removeEventListener("pointerup", soltar); removeEventListener("pointercancel", soltar); };
  }, [arrastre, tam]);

  async function generar() {
    if (!base.current || !caja || !tam) return;
    setGuardado(null);
    setGenerando(true);
    try {
      // recorte que sale: solo la parte baja de la cara
      const k = Math.min(1, ANCHO_ENVIO / caja.w);
      const envio = document.createElement("canvas"); envio.width = Math.round(caja.w * k); envio.height = Math.round(caja.h * k);
      const sx = base.current.naturalWidth / tam.w, sy = base.current.naturalHeight / tam.h;
      envio.getContext("2d")!.drawImage(base.current, caja.x * sx, caja.y * sy, caja.w * sx, caja.h * sy, 0, 0, envio.width, envio.height);
      const r = await a.correr(() => api<{ imagen: string }>("POST", `/api/pacientes/${paciente.id}/simulacion-ia`, { imagen: envio.toDataURL("image/jpeg", 0.9), tipo, nivel, estilo }));
      if (!r) return;
      const nuevo = await cargarImagen(r.imagen);
      // se pega sobre la foto completa con los bordes suavizados (la cara no se toca)
      const lienzo = document.createElement("canvas"); lienzo.width = tam.w; lienzo.height = tam.h;
      const c = lienzo.getContext("2d")!;
      c.drawImage(base.current, 0, 0, tam.w, tam.h);
      const w = Math.round(caja.w), h = Math.round(caja.h);
      const parche = document.createElement("canvas"); parche.width = w; parche.height = h;
      const pc = parche.getContext("2d")!;
      pc.drawImage(nuevo, 0, 0, w, h);
      const datos = pc.getImageData(0, 0, w, h);
      const borde = Math.max(6, Math.round(w * 0.05));
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const ax = Math.min(1, x / borde, (w - 1 - x) / borde), ay = Math.min(1, y / borde, (h - 1 - y) / borde);
        datos.data[(y * w + x) * 4 + 3] = Math.round(255 * Math.min(ax, ay));
      }
      pc.putImageData(datos, 0, 0);
      if (abertura.current) {
        // máscara: solo la abertura de la boca (dientes y encía visibles); labios, piel y todo lo demás quedan como en la foto original
        const m = document.createElement("canvas"); m.width = w; m.height = h;
        const mc = m.getContext("2d")!;
        const px = (p: { x: number; y: number }) => ({ x: p.x - caja.x, y: p.y - caja.y });
        const centro = abertura.current.reduce((t, p) => ({ x: t.x + p.x / abertura.current!.length, y: t.y + p.y / abertura.current!.length }), { x: 0, y: 0 });
        mc.filter = `blur(${Math.max(0.8, w * 0.002)}px)`;
        mc.fillStyle = "#000"; mc.beginPath();
        abertura.current.forEach((p0, i) => {
          // se encoge un poco hacia el centro para no tocar el borde del labio
          const p = px({ x: centro.x + (p0.x - centro.x) * 0.97, y: centro.y + (p0.y - centro.y) * 0.9 });
          if (i === 0) mc.moveTo(p.x, p.y); else mc.lineTo(p.x, p.y);
        });
        mc.closePath(); mc.fill();
        pc.globalCompositeOperation = "destination-in"; pc.drawImage(m, 0, 0); pc.globalCompositeOperation = "source-over";
      }
      c.drawImage(parche, Math.round(caja.x), Math.round(caja.y));
      setDespues(lienzo.toDataURL("image/jpeg", 0.92)); setCorte(50);
    } catch (e) { a.setError(e instanceof Error ? e : new Error("No se pudo generar la imagen.")); }
    finally { setGenerando(false); }
  }

  async function guardar() {
    if (!base.current || !despues || !tam) return;
    const dd = await cargarImagen(despues);
    const rot = tipo === "blanqueamiento" ? "blanqueamiento" : "diseño de sonrisa";
    const letra = Math.round(tam.w / 38), margen = Math.round(letra * 1.6);
    const lienzo = document.createElement("canvas"); lienzo.width = tam.w * 2 + 12; lienzo.height = tam.h + margen;
    const c = lienzo.getContext("2d")!;
    c.fillStyle = "#fff"; c.fillRect(0, 0, lienzo.width, lienzo.height);
    c.drawImage(base.current, 0, margen, tam.w, tam.h); c.drawImage(dd, tam.w + 12, margen, tam.w, tam.h);
    c.fillStyle = "#13302A"; c.font = `bold ${letra}px sans-serif`; c.textBaseline = "middle";
    c.fillText("ANTES", 10, margen / 2); c.fillText(`DESPUÉS · simulación con IA (${rot}) · solo referencia`, tam.w + 22, margen / 2);
    const blob = await new Promise<Blob | null>((res) => lienzo.toBlob(res, "image/jpeg", 0.9));
    if (!blob) return;
    const { imagen, miniatura } = await preparar(new File([blob], "simulacion-ia.jpg", { type: "image/jpeg" }));
    const nota = tipo === "blanqueamiento" ? `Blanqueamiento nivel ${nivel}` : `Diseño de sonrisa ${estilo}`;
    const foto = await a.correr(() => api<{ id: string }>("POST", `/api/pacientes/${paciente.id}/fotos`, { tipo: "otro", etiqueta: `Simulación con IA · ${rot}`, nota: `Simulación orientativa con IA: ${nota}, sobre «${etiqueta}»`.slice(0, 300), imagen, miniatura }));
    if (foto) { setGuardado(foto); alGuardar(); }
  }

  return (
    <div className="visor visor-ia" role="dialog" aria-modal="true" aria-label="Sonrisa con IA">
      <div className="visor-ia-cuerpo">
        <div className="visor-ia-imagen">
          <div className="visor-ia-marco" style={tam ? { width: `min(100%, calc(86vh * ${tam.w / tam.h}))` } : undefined}>
            {tam && caja ? (
              <>
                {despues ? (
                  <div style={{ position: "relative", width: "100%", aspectRatio: `${tam.w} / ${tam.h}`, userSelect: "none" }}>
                    <img src={despues} alt="Después (simulación con IA)" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
                    <img src={url} alt="Antes" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", clipPath: `inset(0 ${100 - corte}% 0 0)` }} />
                    <div style={{ position: "absolute", top: 0, bottom: 0, left: `${corte}%`, width: 3, background: "#fff", boxShadow: "0 0 4px #000" }} aria-hidden="true" />
                    <span className="chip" style={{ position: "absolute", left: 8, top: 8 }}>ANTES</span>
                    <span className="chip azul" style={{ position: "absolute", right: 8, top: 8 }}>DESPUÉS · SIMULACIÓN</span>
                  </div>
                ) : (
                  <svg ref={svg} viewBox={`0 0 ${tam.w} ${tam.h}`} style={{ width: "100%", aspectRatio: `${tam.w} / ${tam.h}`, display: "block", touchAction: "none", userSelect: "none" }} role="img" aria-label="Foto del paciente con el recuadro que se enviará a la IA">
                    <image href={url} x={0} y={0} width={tam.w} height={tam.h} />
                    <rect x={0} y={0} width={tam.w} height={tam.h} fill="rgba(0,0,0,.35)" />
                    <image href={url} x={0} y={0} width={tam.w} height={tam.h} clipPath="url(#recorte-ia)" />
                    <defs><clipPath id="recorte-ia"><rect x={caja.x} y={caja.y} width={caja.w} height={caja.h} /></clipPath></defs>
                    <rect x={caja.x} y={caja.y} width={caja.w} height={caja.h} fill="transparent" stroke="#fbbf24" strokeWidth={Math.max(2, tam.w / 400)} style={{ cursor: "move" }}
                      onPointerDown={(ev) => { ev.preventDefault(); const p = aPixeles(ev); setArrastre({ modo: "mover", ox: p.x, oy: p.y, caja0: caja }); }} />
                    <circle cx={caja.x + caja.w} cy={caja.y + caja.h} r={Math.max(10, tam.w / 90)} fill="#fbbf24" stroke="#fff" strokeWidth={2} style={{ cursor: "nwse-resize" }}
                      onPointerDown={(ev) => { ev.preventDefault(); const p = aPixeles(ev); setArrastre({ modo: "esquina", ox: p.x, oy: p.y, caja0: caja }); }} />
                  </svg>
                )}
                {despues && <input type="range" min={0} max={100} value={corte} onChange={(e) => setCorte(Number(e.target.value))} aria-label="Comparar antes y después" style={{ width: "100%" }} />}
              </>
            ) : <p className="muted" style={{ color: "#fff", padding: 24 }}>Preparando la foto…</p>}
          </div>
        </div>

        <aside className="visor-ia-lado card">
          <div className="fila" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ margin: 0, fontSize: 17 }}>Sonrisa con IA</h2>
            <button className="btn chico" onClick={alCerrar}>Cerrar</button>
          </div>
          <p className="muted" style={{ margin: "2px 0", fontSize: 12 }}>
            Imagen de referencia generada por IA: es una idea aproximada de cómo podría quedar, no una promesa de resultado. Solo se envía a Google el recuadro de la boca (de la nariz al mentón), sin nombre ni datos del paciente.
          </p>
          {listaIA === false && <div className="aviso">La clínica todavía no configuró la clave de Gemini. Un administrador la agrega en <a href="#/configuracion/gemini">Configuración → IA de imágenes</a>.</div>}
          {buscando && <div className="aviso info" role="status">Donna está buscando la boca en la foto…</div>}
          {aviso && !buscando && !despues && <div className="aviso info" role="status">{aviso}</div>}

          <div className="campo">Qué quieres ver
            <div className="fila" style={{ gap: 4, flexWrap: "wrap" }}>
              <button className={`btn chico${tipo === "blanqueamiento" ? " pri" : ""}`} aria-pressed={tipo === "blanqueamiento"} onClick={() => setTipo("blanqueamiento")}>Blanqueamiento</button>
              <button className={`btn chico${tipo === "sonrisa" ? " pri" : ""}`} aria-pressed={tipo === "sonrisa"} onClick={() => setTipo("sonrisa")}>Diseño de sonrisa</button>
            </div>
          </div>
          {tipo === "blanqueamiento" ? (
            <div className="campo">Qué tanto
              <div className="fila" style={{ gap: 4, flexWrap: "wrap" }}>{NIVELES.map(([k, t]) => <button key={k} className={`btn chico${nivel === k ? " pri" : ""}`} aria-pressed={nivel === k} onClick={() => setNivel(k)}>{t}</button>)}</div>
            </div>
          ) : (
            <div className="campo">Estilo
              <div className="fila" style={{ gap: 4, flexWrap: "wrap" }}>{ESTILOS.map(([k, t]) => <button key={k} className={`btn chico${estilo === k ? " pri" : ""}`} aria-pressed={estilo === k} onClick={() => setEstilo(k)}>{t}</button>)}</div>
            </div>
          )}
          <AvisoError error={a.error} />
          <div className="fila" style={{ flexWrap: "wrap" }}>
            <button className="btn pri" onClick={generar} disabled={generando || a.ocupado || !caja || buscando || listaIA === false}>{generando ? "Generando… (puede tardar unos segundos)" : despues ? "Generar otra vez" : "Generar imagen"}</button>
            {despues && !guardado && <button className="btn" onClick={guardar} disabled={a.ocupado}>Guardar como simulación</button>}
            {despues && <button className="btn chico" onClick={() => { setDespues(null); setGuardado(null); }}>Mover el recuadro</button>}
          </div>
          <small className="muted">Cada imagen que se genera la cobra Google a tu clínica (unos 3 o 4 centavos de dólar). Si no te convence, puedes generar otra.</small>
          {guardado && (
            <div className="aviso ok" role="status" style={{ display: "grid", gap: 6 }}>
              <b>Simulación guardada</b> como foto rotulada, con el antes y el después. Ya puedes darle al paciente su PDF:
              <span className="fila" style={{ flexWrap: "wrap", gap: 6 }}>
                <GuardarPdf tipo="simulacion" id={guardado.id} nombre={`Simulacion-${paciente.apellidos || paciente.nombres}`} etiqueta="Guardar PDF" />
                <EnviarWhatsApp tipo="simulacion" id={guardado.id} />
              </span>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
